import React from 'react';
import styled from 'styled-components';
import { Body5, Container } from '@ui';
import { SkeletonButton } from '@ui/buttons';
import { DEFAULT_PAGE_SIZE } from '@services/fmdc';
import { formatShownRange } from './format';

// Server-side paging over `{ limit, offset, total }`. The visual is the vault's client-side
// Pagination (Previous · "Page X of Y" · Next); the range line says what is on screen because a
// reviewer working a 491-row queue needs to know how far along they are, not only which page.

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

// Clamp an offset so a page that emptied (a decided row left the Open list) does not strand the
// view past the end. Exported for the hooks that re-fetch after a write.
export const clampOffset = (offset, total, limit = DEFAULT_PAGE_SIZE) => {
  if (!total || total <= 0) return 0;
  const lastPageOffset = Math.floor((total - 1) / limit) * limit;
  return Math.max(0, Math.min(offset, lastPageOffset));
};

export const Pager = ({ offset = 0, limit = DEFAULT_PAGE_SIZE, total = 0, count, onChange }) => {
  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const shown = count !== undefined ? count : Math.min(limit, Math.max(0, total - offset));

  if (!total) return null;

  return (
    <Container top={24} column>
      <Wrapper>
        {/* `disabled` on the shared buttons only styles them, so the page guard stays in the handler. */}
        <SkeletonButton disabled={page <= 1} onClick={() => page > 1 && onChange(Math.max(0, offset - limit))}>
          Previous
        </SkeletonButton>
        <Body5 isLowContrast>
          Page {page} of {pageCount}
        </Body5>
        <SkeletonButton disabled={page >= pageCount} onClick={() => page < pageCount && onChange(offset + limit)}>
          Next
        </SkeletonButton>
      </Wrapper>
      <Container top={8} textCenter>
        <Body5 isLowContrast center>
          {formatShownRange(offset, shown, total)}
        </Body5>
      </Container>
    </Container>
  );
};
